import { useEffect, useId, useState } from 'react'
import { ArrowDown, ArrowUp, ChartNoAxesColumn, Minus, RefreshCw, Table2, TriangleAlert } from 'lucide-react'
import Button from '../../components/Button'
import Chart from '../../components/Chart'
import IconButton from '../../components/IconButton'
import InfoHint from '../../components/InfoHint'
import Skeleton from '../../components/Skeleton'
import { DeniedState, EmptyState, ErrorState } from '../../components/States'
import { HTTP_STATUS } from '../../domain/domainCodes'
import { DASH, change, num, ofN, pct } from './reportFormat'
import { indicatorIcon } from './reportIcons'
import { metricHue } from './reportPalette'
import { strings as S } from './strings'

/**
 * The pieces every Reports section is built from, so a rate, a change and a flag look the same
 * in each of them.
 */

/** "12 / 340 رسالة" — the counts behind a rate, with what one unit is. */
export function Counts({ rate, unit }) {
  if (rate?.denominator == null) return null
  return (
    <span className="rp-counts">
      <bdi dir="ltr">{ofN(rate)}</bdi> {unit ? S.units[unit] ?? '' : ''}
    </span>
  )
}

/**
 * A change in points, said as a word — "انخفض 3.1 نقطة عن الفترة السابقة".
 *
 * Neutral on purpose (plan §5.7): without a target the organisation has set, "down" is not a
 * verdict, so the colour stays the text colour and the arrow only echoes the word.
 */
export function Change({ delta, higherIsBetter = true, compare = 'previous', unit = 'points', against: withAgainst = true }) {
  const moved = change(delta, higherIsBetter)
  if (!moved) return null
  const Icon = moved.direction === 'up' ? ArrowUp : moved.direction === 'down' ? ArrowDown : Minus
  const word = S.delta[moved.direction]
  /* Said only against a period; a change inside one view (baseline → follow-up) needs no tail. */
  const against = !withAgainst ? null : compare === 'year' ? S.delta.year : compare === 'previous' ? S.delta.previous : null
  return (
    <span className="rp-change">
      <Icon className="rp-change__icon" size={14} strokeWidth={2} aria-hidden="true" />
      <span>
        {word}
        {moved.direction !== 'flat' && (
          <>
            {' '}
            <bdi dir="ltr">{moved.points}</bdi> {unit === 'points' ? S.delta.points : unit}
          </>
        )}
        {against && (
          <>
            {' '}
            <span className="rp-change__against">{against}</span>
          </>
        )}
      </span>
    </span>
  )
}

/** "▲ 3 متأخرة" — how many rows are notably behind, as a rose chip beside a panel's title. */
export function BehindBadge({ count, title }) {
  if (!count) return null
  return (
    <span className="rp-badge rp-badge--bad" title={title}>
      <TriangleAlert size={12} strokeWidth={2.5} aria-hidden="true" />
      <bdi dir="ltr">{num(count)}</bdi> {S.cell.behind}
    </span>
  )
}

/** "متأخرة" — an icon and a word, never a colour alone. */
export function BehindFlag() {
  return (
    <span className="rp-flag" title={S.cell.behindHint}>
      <TriangleAlert size={13} strokeWidth={2.25} aria-hidden="true" />
      {S.cell.behind}
    </span>
  )
}

/**
 * A rate inside a breakdown: the figure, its counts, and the "behind" flag when the department is
 * notably behind the organisation. Withheld below the minimum group size, and never coloured
 * alone — the flag is an icon and a word.
 */
export function RateCell({ rate, k, counts = true }) {
  if (!rate) return <span className="rp-muted">{DASH}</span>
  if (rate.suppressed) {
    return (
      <span className="rp-muted" title={S.cell.fewerHint}>
        {S.cell.fewer} <bdi dir="ltr">{k ?? ''}</bdi>
      </span>
    )
  }
  if (rate.value == null) return <span className="rp-muted">{DASH}</span>
  /* The counts behind the rate: a second line when asked for, otherwise the tooltip. */
  const tip = [rate.behind ? S.cell.behindHint : null, !counts && rate.denominator != null ? ofN(rate) : null]
    .filter(Boolean)
    .join(' · ')
  return (
    <span className={`rp-rate${rate.behind ? ' is-behind' : ''}`} title={tip || undefined}>
      <span className="rp-rate__line">
        {rate.behind && <span className="rp-rate__flag" aria-hidden="true" />}
        <bdi className="rp-rate__value" dir="ltr">
          {pct(rate.value)}
        </bdi>
        {rate.behind && <span className="rp-visually-hidden">{S.cell.behind}</span>}
      </span>
      {counts && rate.denominator != null && (
        <bdi className="rp-rate__n" dir="ltr">
          {ofN(rate)}
        </bdi>
      )}
    </span>
  )
}

/**
 * A rate in the department matrix, washed by how far it sits from the organisation — teal on the
 * better side, coral on the worse, a little deeper the further (to 25 points; never more than a tint) — so the matrix reads as a
 * heatmap before a figure is read. The wash only echoes the figure; "behind" is still said by the
 * triangle and, for a screen reader, the word.
 */
export function HeatCell({ rate, organisation, higherIsBetter = true, k, counts }) {
  const value = rate?.suppressed ? null : rate?.value
  const gap = value == null || organisation == null ? 0 : (Number(value) - Number(organisation)) * (higherIsBetter ? 1 : -1)
  /* Three steps, not a continuous wash: a reader sees "a little", "clearly" and "far" at a glance,
     and the palest step stays a whisper. */
  const size = Math.abs(gap)
  const step = size < 2 ? 0 : size < 5 ? 1 : size < 10 ? 2 : 3
  const tooltip = rate?.denominator != null && !rate.suppressed ? `${ofN(rate)}` : undefined
  return (
    <span
      className={`rp-heat${step ? ` rp-heat--${gap > 0 ? 'good' : 'bad'} rp-heat--${step}` : ''}`}
      title={tooltip}
    >
      <RateCell rate={rate} k={k} counts={counts} />
    </span>
  )
}

/**
 * Where a rate stands on 0–100%, and where it stood before (or where the organisation stands) —
 * in the indicator's own hue. The fill is the reading; the tick is the comparison.
 */
export function RateMeter({ value, previous, label, tickTitle }) {
  if (value == null) return null
  const clamp = (n) => Math.max(0, Math.min(100, Number(n)))
  return (
    <span className="rp-meter" role="img" aria-label={label}>
      <span className="rp-meter__fill" style={{ inlineSize: `${clamp(value)}%` }} />
      {previous != null && (
        <span className="rp-meter__tick" style={{ insetInlineStart: `${clamp(previous)}%` }} title={tickTitle ?? `${S.delta.before} ${pct(previous)}`} />
      )}
    </span>
  )
}

/**
 * A change as a small soft chip — an arrow and "2.3 نقطة" — teal when it went the good way for
 * this indicator, coral when it did not, grey for none. The words ("انخفض 2.3 نقطة عن الفترة
 * السابقة") are its accessible name and its tooltip, so neither the colour nor the arrow is ever
 * the only way to read it.
 *
 * @param {string} [against]  what the change is measured against, said in the tooltip only
 */
export function DeltaPill({ delta, higherIsBetter = true, unit = 'points', words: said, compact = false, against }) {
  const moved = change(delta, higherIsBetter)
  if (!moved) return null
  const tone = moved.direction === 'flat' ? 'flat' : moved.favourable ? 'good' : 'bad'
  const unitWord = unit === 'points' ? S.delta.points : unit
  const words =
    said ??
    `${S.delta[moved.direction]}${moved.direction === 'flat' ? '' : ` ${moved.points} ${unitWord}`}${against ? ` ${against}` : ''}`
  const Icon = moved.direction === 'up' ? ArrowUp : moved.direction === 'down' ? ArrowDown : Minus
  return (
    <span className={`rp-delta rp-delta--${tone}`} title={words} aria-label={words}>
      <Icon className="rp-delta__arrow" size={12} strokeWidth={2.5} aria-hidden="true" />
      {moved.direction !== 'flat' ? (
        <>
          <bdi dir="ltr">{moved.points}</bdi>
          {unitWord && !compact ? <span className="rp-delta__unit">{unitWord}</span> : null}
        </>
      ) : (
        <span>{S.delta.flat}</span>
      )}
    </span>
  )
}

/**
 * A headline figure — "10.1%" with the per-cent sign set smaller, so the eye takes the number
 * first. Anything that is not a percentage prints as it is.
 */
export function Figure({ text, className = 'rp-kpi__value' }) {
  const value = String(text ?? '')
  const unit = value.endsWith('%') ? '%' : null
  return (
    <bdi className={className} dir="ltr">
      {unit ? value.slice(0, -1) : value}
      {unit && <span className="rp-kpi__unit">{unit}</span>}
    </bdi>
  )
}

/* A smooth line through the points (Catmull-Rom as cubic Béziers), so a sparkline reads as a
   shape rather than a zig-zag of segments. */
const smoothPath = (pts) =>
  pts
    .map(([x, y], i) => {
      if (i === 0) return `M${x.toFixed(1)},${y.toFixed(1)}`
      const [x0, y0] = pts[i - 2] ?? pts[i - 1]
      const [x1, y1] = pts[i - 1]
      const [x3, y3] = pts[i + 1] ?? [x, y]
      const t = 0.18
      const c1 = [x1 + (x - x0) * t, y1 + (y - y0) * t]
      const c2 = [x - (x3 - x1) * t, y - (y3 - y1) * t]
      return `C${c1[0].toFixed(1)},${c1[1].toFixed(1)} ${c2[0].toFixed(1)},${c2[1].toFixed(1)} ${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

const SPARK_POINTS = 12
const SPARK_MIN_SPAN = 20

/**
 * The indicator's recent readings as a small line in its hue beside the figure, oldest to newest
 * left to right (time runs that way on every chart here), with a faint wash under it. Decorative: the figure and the section's own charts carry the values,
 * so it is hidden from assistive technology.
 */
export function Sparkline({ values, color, width = 112, height = 40 }) {
  const gradient = `rp-spark-${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  /* The latest readings only, and never less than twenty points of scale: twelve campaigns that
     wander between 8% and 14% drawn edge to edge are a seismograph, not a trend. */
  const points = (values ?? [])
    .filter((value) => value != null && Number.isFinite(Number(value)))
    .map(Number)
    .slice(-SPARK_POINTS)
  if (points.length < 2) return null
  const low = Math.min(...points)
  const high = Math.max(...points)
  const pad = Math.max(0, SPARK_MIN_SPAN - (high - low)) / 2
  const min = Math.max(0, low - pad)
  const max = Math.min(100, Math.max(high + pad, min + SPARK_MIN_SPAN))
  const span = max - min || 1
  const inset = 4
  const xy = points.map((value, index) => [
    inset + (index / (points.length - 1)) * (width - inset * 2),
    inset + (1 - (value - min) / span) * (height - inset * 2),
  ])
  const line = smoothPath(xy)
  const [lastX, lastY] = xy[xy.length - 1]
  /* The drawing scales to whatever width the card leaves it (`preserveAspectRatio="none"`); the
     stroke keeps its weight through `vector-effect`. */
  /* The latest reading as a dot. The line stretches to its box, so a circle drawn inside the SVG
     would stretch with it; the dot is a box of its own, placed at the same point. */
  return (
    <span className="rp-spark" aria-hidden="true" dir="ltr">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${line} L${xy[xy.length - 1][0]},${height} L${xy[0][0]},${height} Z`} fill={`url(#${gradient})`} />
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        className="rp-spark__end"
        style={{ left: `${(lastX / width) * 100}%`, top: `${(lastY / height) * 100}%`, background: color }}
      />
    </span>
  )
}

/**
 * One headline indicator as a card — the same three parts on every card, so a row of them reads
 * at a glance: the area's icon in a soft chip with the indicator's name; the figure, large, with
 * its recent readings as a small line beside it; and the change as a chip. Nothing else is
 * printed — the definition, the counts behind the rate, the comparison figure and any secondary
 * reading sit behind the (i).
 *
 * @param {number[]} [series]  recent readings, oldest first
 * @param {import('react').ReactNode} [note]  extra lines for the (i)
 */
export function IndicatorCard({ indicator, compare, series, note, loading = false }) {
  const label = S.indicators[indicator?.key] ?? indicator?.key
  if (loading || !indicator) {
    return (
      <article className="rp-kpi" aria-busy="true">
        <Skeleton height={14} width="55%" />
        <Skeleton height={32} width="45%" />
        <Skeleton height={20} width="35%" />
      </article>
    )
  }
  const color = metricHue(indicator.key)
  const Icon = indicatorIcon(indicator.key)
  const available = indicator.available && indicator.current?.value != null
  const previous = compare !== 'none' ? indicator.previous?.value : null
  const against = compare === 'year' ? S.delta.year : compare === 'previous' ? S.delta.previous : null
  const info = (
    <span className="rp-tile__info">
      {S.definitions[indicator.key] && <span>{S.definitions[indicator.key]}</span>}
      {available && indicator.current?.denominator != null && (
        <span>
          <bdi dir="ltr">{ofN(indicator.current)}</bdi> {S.units[indicator.unit] ?? ''}
        </span>
      )}
      {previous != null && (
        <span>
          {against}: <bdi dir="ltr">{pct(previous)}</bdi>
        </span>
      )}
      {indicator.secondary && indicator.secondaryKey && (
        <span>
          {S.indicators[indicator.secondaryKey]}:{' '}
          <bdi dir="ltr">{indicator.secondary.value == null ? DASH : pct(indicator.secondary.value)}</bdi>
        </span>
      )}
      {note}
    </span>
  )
  const showChange = available && compare !== 'none' && indicator.deltaPoints != null
  return (
    <article className="rp-kpi" style={{ '--hue': color }}>
      <header className="rp-kpi__head">
        <span className="rp-kpi__icon" aria-hidden="true">
          <Icon size={16} strokeWidth={2} />
        </span>
        <h3 className="rp-kpi__label">
          <bdi>{label}</bdi>
        </h3>
        <InfoHint content={info} />
      </header>
      <div className="rp-kpi__body">
        <Figure text={available ? pct(indicator.current.value) : DASH} />
        {available && <Sparkline values={series} color={color} />}
      </div>
      <div className="rp-kpi__foot">
        {!available ? (
          <span className="rp-kpi__reason">{S.reasons[indicator.reason] ?? S.reasons.noData}</span>
        ) : showChange ? (
          <DeltaPill delta={indicator.deltaPoints} higherIsBetter={indicator.higherIsBetter} against={against} />
        ) : null}
      </div>
    </article>
  )
}

/**
 * A section's headline: its indicators as cards, then — on one quiet strip — the figures that are
 * not rates and the ways to the names behind them.
 *
 * @param {Record<string, number[]>} [series]  sparkline readings per indicator key
 * @param {import('react').ReactNode} [note]  lines for the first card's (i)
 */
export function Ledger({ indicators, stats, compare, loading, count = 5, note, links, series = {} }) {
  const items = loading || !indicators ? Array.from({ length: count }, () => null) : indicators
  return (
    <section className="rp-overview" aria-label={S.summary.indicators}>
      <div className="rp-kpis" style={{ '--rp-kpis': items.length }}>
        {items.map((indicator, index) => (
          <IndicatorCard
            key={indicator?.key ?? index}
            indicator={indicator}
            compare={compare}
            series={indicator ? series[indicator.key] : null}
            note={index === 0 ? note : null}
            loading={loading || !indicator}
          />
        ))}
      </div>
      {(stats !== undefined || links) && (
        <div className="rp-strip">
          {stats !== undefined && <StatList stats={stats} compare={compare} loading={loading || !stats} />}
          {links && <div className="rp-strip__links">{links}</div>}
        </div>
      )}
    </section>
  )
}

/** The row of headline indicators alone — the overview's five. */
export function IndicatorRow({ indicators, compare, loading, count = 5, note, series }) {
  return <Ledger indicators={indicators} compare={compare} loading={loading} count={count} note={note} series={series} />
}

/** "3 حملات: 1 سهل · 2 صعب" — what the phishing figures were measured on. */
export function CampaignMix({ mix }) {
  if (!mix?.campaigns) return null
  const parts = [
    ['easy', mix.easy],
    ['normal', mix.normal],
    ['hard', mix.hard],
    ['unrated', mix.unrated],
  ].filter(([, count]) => count > 0)
  return (
    <span className="rp-mix">
      {S.mix.measuredOn} <bdi dir="ltr">{num(mix.campaigns)}</bdi> {S.mix.campaigns}
      {parts.length > 0 && ': '}
      {parts.map(([key, count], index) => (
        <span key={key}>
          {index > 0 ? ' · ' : ''}
          <bdi dir="ltr">{num(count)}</bdi> {S.mix[key]}
        </span>
      ))}
    </span>
  )
}

/* Units worth printing beside a figure; a count of people is already named by its label. */
const MEDIANS = new Set(['minute', 'hour', 'day', 'attempt'])

/**
 * Figures that are not proportions — counts and medians — as one strip of columns, each its
 * name over its figure, divided by hairlines (the reference board's summary band). A count shows
 * what it is out of when that is a larger population ("4 / 120"); a median shows its unit.
 */
export function StatList({ stats, compare, loading }) {
  if (loading || !stats) {
    return (
      <div className="rp-stats">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="rp-stat">
            <Skeleton height={12} width="70%" />
            <Skeleton height={22} width="40%" />
          </div>
        ))}
      </div>
    )
  }
  return (
    <dl className="rp-stats" style={{ '--rp-stats': stats.length }}>
      {stats.map((stat) => {
        const median = MEDIANS.has(stat.unit)
        return (
          <div key={stat.key} className="rp-stat">
            <dt className="rp-stat__label">{S.stats[stat.key] ?? stat.key}</dt>
            <dd className="rp-stat__value">
              {/* "4 / 120" is one figure, so it is one LTR run — split, Arabic flips it. */}
              <bdi dir="ltr">
                {stat.value == null ? DASH : num(stat.value)}
                {stat.value != null && !median && stat.n > stat.value && (
                  <span className="rp-stat__unit"> / {num(stat.n)}</span>
                )}
              </bdi>
              {stat.value != null && median && <span className="rp-stat__unit"> {S.units[stat.unit]}</span>}
            </dd>
            {compare !== 'none' && stat.delta != null && stat.delta !== 0 && (
              <dd className="rp-stat__change">
                <DeltaPill delta={stat.delta} higherIsBetter={stat.higherIsBetter} unit={median ? S.units[stat.unit] : ''} compact={!median} />
              </dd>
            )}
          </div>
        )
      })}
    </dl>
  )
}

/**
 * A section's heading — for assistive technology only. The active tab already names the section
 * on screen, so a second visible title would be the same word twice; the question it answers
 * and its caveats travel in the first card's (i).
 */
export function SectionHead({ id, title }) {
  return (
    <h2 className="rp-visually-hidden" id={id}>
      {title}
    </h2>
  )
}

/**
 * A titled panel. `actions` sits in the header; `hint` goes behind an (i).
 */
export function Panel({ title, caption, subtitle, hint, badge, actions, children, className = '' }) {
  /* A panel prints its title and its content. What the chart shows, its scope and how to read it
     wait behind the (i): the title has to carry the panel on its own. */
  const info =
    caption || subtitle || hint ? (
      <span className="rp-tile__info">
        {caption && <span>{caption}</span>}
        {subtitle && <span>{subtitle}</span>}
        {hint && <span>{hint}</span>}
      </span>
    ) : null
  return (
    <section className={`card rp-panel ${className}`.trim()}>
      <header className="rp-panel__head">
        <div className="rp-panel__titles">
          <h3 className="rp-panel__title">
            <bdi>{title}</bdi>
            {info && <InfoHint content={info} />}
          </h3>
          {badge}
        </div>
        {actions && <div className="rp-panel__actions">{actions}</div>}
      </header>
      <div className="rp-panel__body">{children}</div>
    </section>
  )
}

/**
 * A chart with the same numbers as a table one click away (plan §6.7). The table is a real
 * table, not a tooltip dump, and `summary` names what the chart shows for a screen reader.
 *
 * BIG DATA. A long ranking arrives windowed — the first rows and a scrollbar (`rowZoom`) — and
 * `more` offers the whole of it at full height for the reader who wants to see every row at
 * once; the window comes back with one more click.
 *
 * @param {{ total: number, option: object, height: number }} [more]  the chart drawn whole
 * @param {import('react').ReactNode} [controls]  a switch that changes what is plotted
 * @param {import('react').ReactNode} [footer]  a line under the plot — what was left out, and why
 * @param {(params: object) => void} [onSelect]  a click on a mark
 */
export function ChartPanel({
  title,
  caption,
  subtitle,
  hint,
  badge,
  option,
  height = 300,
  summary,
  table,
  empty,
  loading,
  more,
  controls,
  footer,
  onSelect,
}) {
  const [asTable, setAsTable] = useState(false)
  const [whole, setWhole] = useState(false)
  const ready = !loading && !empty
  const expanded = whole && more && !asTable

  const actions = ready ? (
    <>
      {controls}
      {table && (
        <IconButton
          icon={asTable ? ChartNoAxesColumn : Table2}
          label={asTable ? S.actions.showChart : S.actions.showTable}
          tone="brand"
          onClick={() => setAsTable((value) => !value)}
          aria-pressed={asTable}
        />
      )}
    </>
  ) : null

  let body
  if (loading) body = <Skeleton height={height} />
  else if (empty) body = <EmptyState title={empty} />
  else if (asTable) body = table
  else
    body = (
      <Chart
        option={expanded ? more.option : option}
        height={expanded ? more.height : height}
        ariaLabel={summary || title}
        onSelect={onSelect}
      />
    )

  return (
    <Panel title={title} caption={caption} subtitle={subtitle} hint={hint} badge={badge} actions={actions} className="rp-panel--chart">
      {body}
      {ready && !asTable && footer && <p className="rp-chart-foot">{footer}</p>}
      {/* The way to the rest of a windowed chart sits under it, where the window ends. */}
      {ready && more && !asTable && (
        <div className="rp-chart-more">
          <button type="button" className="rp-more" onClick={() => setWhole((value) => !value)} aria-pressed={whole}>
            {whole ? (
              S.table.showLess
            ) : (
              <>
                {S.table.showAll} <bdi dir="ltr">({num(more.total)})</bdi>
              </>
            )}
          </button>
        </div>
      )}
    </Panel>
  )
}

/**
 * Parts of one whole as ONE bar — so the shares are read against each other — with each part's
 * count and share in the key beneath it. For mutually exclusive outcomes only: incident reviews,
 * an assessment's audience. `tone` is a chart status token (success, failure, incomplete,
 * absent, info, info-soft).
 *
 * @param {{ key: string, label: string, value: number, tone: string }[]} parts
 */
export function SplitBar({ parts, label }) {
  const total = parts.reduce((sum, part) => sum + (part.value || 0), 0)
  const share = (value) => (total ? Math.round((value / total) * 1000) / 10 : null)
  return (
    <div className="rp-split">
      <div className="rp-split__bar" role="img" aria-label={label}>
        {parts
          .filter((part) => part.value > 0)
          .map((part) => (
            <span key={part.key} className={`rp-split__part rp-split__part--${part.tone}`} style={{ flexGrow: part.value }} />
          ))}
      </div>
      <ul className="rp-split__key">
        {parts.map((part) => (
          <li key={part.key} className="rp-split__item">
            <span className={`rp-split__swatch rp-split__part--${part.tone}`} aria-hidden="true" />
            <span className="rp-split__label">{part.label}</span>
            <span className="rp-split__value">
              <bdi dir="ltr">{num(part.value)}</bdi>
            </span>
            <span className="rp-split__share">
              <bdi dir="ltr">{total ? pct(share(part.value)) : DASH}</bdi>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A short caveat that applies to what is on screen. */
export function Caveat({ children }) {
  return (
    <p className="rp-caveat">
      <TriangleAlert className="rp-caveat__icon" size={15} strokeWidth={2} aria-hidden="true" />
      <span>{children}</span>
    </p>
  )
}

/**
 * Loading, failure, denial and a bad scope — for one section, so a failure in one never blanks
 * the others (plan §6.5). A server failure is never drawn as zeroes.
 */
export function SectionStatus({ error, onRetry, loading }) {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    if (!loading) {
      setSlow(false)
      return undefined
    }
    const timer = setTimeout(() => setSlow(true), 6000)
    return () => clearTimeout(timer)
  }, [loading])

  if (error) {
    if (error.status === HTTP_STATUS.FORBIDDEN || error.denied) return <DeniedState />
    if (error.status === HTTP_STATUS.BAD_REQUEST) {
      return <EmptyState title={S.states.invalidScope} description={S.states.invalidScopeHint} />
    }
    return (
      <ErrorState
        title={S.states.loadFailed}
        description={S.states.loadFailedHint}
        action={
          onRetry ? (
            <Button variant="soft" size="sm" onClick={onRetry}>
              <RefreshCw size={15} strokeWidth={1.75} />
              {S.actions.retry}
            </Button>
          ) : null
        }
      />
    )
  }

  if (loading && slow) {
    return (
      <p className="rp-slow" role="status" aria-live="polite">
        {S.states.slow}
      </p>
    )
  }
  return null
}
